/**
 * ListRow — one item in a section list (a job, a vehicle, an invoice): a
 * `cardTitle` and an optional status on the first line, then whatever follows
 * (usually a FieldList and a body line). On the page ground `bg` so each item
 * stands apart from its `surface` box. The whole row is the tap target when
 * `onClick` is set (Enter opens it too). `tone` marks the row: `caution` an
 * amber edge only, `warning` an amber tint and edge, `danger` a red one (a car
 * too long in a department); the words inside still carry the meaning. Same
 * prop as the app's ListRow.
 */
import React from 'react';
import { sectionSpec, textRole, tokenVars } from '@turn-connect/ui';

export interface ListRowProps {
  title: React.ReactNode;
  /** Right of the title: a StatusPill. */
  status?: React.ReactNode;
  onClick?: () => void;
  /** Shown as chosen (a picked vehicle). */
  selected?: boolean;
  /** `caution`: an amber edge; `warning` / `danger`: tinted ground and edge (ignored while `selected`). */
  tone?: 'caution' | 'warning' | 'danger';
  /** `button` makes a real <button> (use when the row has no buttons inside). */
  as?: 'div' | 'button';
  testId?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
}

const R = sectionSpec.row;

/** A toned row's colour mixed into the page ground (solid, as the app's mixHex). */
const toneGround = (c: string) => `color-mix(in srgb, ${c} ${Math.round(R.toneShare * 100)}%, ${tokenVars.bg})`;
const TONE: Record<NonNullable<ListRowProps['tone']>, { ground: string; edge: string }> = {
  caution: { ground: tokenVars.bg, edge: tokenVars.warning },
  warning: { ground: toneGround(tokenVars.warning), edge: tokenVars.warning },
  danger: { ground: toneGround(tokenVars.danger), edge: tokenVars.danger },
};

/** A row's ground and edge for a tone (a legend's swatch draws the same). */
export const listRowLook = (tone?: ListRowProps['tone']): { ground: string; edge: string } =>
  tone ? TONE[tone] : { ground: tokenVars.bg, edge: tokenVars.borderSubtle };

export const ListRow: React.FC<ListRowProps> = ({ title, status, onClick, selected, tone, as = 'div', testId, ariaLabel, children }) => {
  const toned = !selected && tone ? TONE[tone] : null;
  const style: React.CSSProperties = {
    display: 'grid',
    gap: R.gap,
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    font: 'inherit',
    color: tokenVars.text.primary,
    background: selected ? tokenVars.brandSubtle : listRowLook(tone).ground,
    border: `1px solid ${selected ? tokenVars.brand : listRowLook(tone).edge}`,
    borderRadius: R.radius,
    padding: `${R.paddingVertical}px ${R.paddingHorizontal}px`,
    cursor: onClick ? 'pointer' : undefined,
    minWidth: 0,
  };
  const body = (
    <>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ ...textRole('cardTitle'), minWidth: 0, overflowWrap: 'anywhere' }}>{title}</span>
        {status}
      </span>
      {children}
    </>
  );
  if (as === 'button') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        aria-label={ariaLabel}
        style={style}
        data-testid={testId}
        data-tone={toned ? tone : undefined}
      >
        {body}
      </button>
    );
  }
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={style}
      data-testid={testId}
      data-tone={toned ? tone : undefined}
    >
      {body}
    </div>
  );
};

export default ListRow;
